import { ReactNode } from 'react';
import { Minifiable } from '@optimus-web/common';
import { IconName } from '@optimus-web/icons';
import { OptimusTextKey } from '@optimus-web/localization';
import {
    StepElement,
    StepIconElement,
    StepNumberElement,
    StepPrimaryTextElement,
    StepSecondaryTextElement,
    StepTextWrapper,
} from './style';

export interface StepDescription<S extends string = string> {
    value: S;
    label: string | ReactNode;
    optional?: boolean;
    active?: boolean;
    completed?: boolean;
    editable?: boolean;
    order?: number;
    ariaLabel?: string;
}

export interface StepProps<S extends string = string> extends StepDescription<S>, Minifiable {
    index: number;
    onClick?: (value: S) => void;
    vertical?: boolean;
    singleItem?: boolean;
}

export const Step = <S extends string = string>({
    index,
    label,
    ariaLabel,
    value,
    optional,
    completed,
    editable,
    vertical,
    active,
    small,
    singleItem,
    onClick,
    ...other
}: StepProps<S>) => {
    const isClickable = !active && editable;

    const handleClick = () => {
        if (onClick) {
            onClick(value);
        }
    };

    return (
        <li {...other}>
            <StepElement
                type="button"
                as={isClickable ? 'button' : 'div'}
                onClick={isClickable ? handleClick : undefined}
                active={active}
                completed={completed}
                clickable={isClickable}
                vertical={vertical}
                small={small}
                aria-current={active ? true : undefined}
                data-test-step={value}
                aria-label={ariaLabel}
            >
                {!completed || active ? (
                    <StepNumberElement
                        active={active}
                        completed={completed}
                        singleItem={singleItem}
                        clickable={isClickable}
                    >
                        {active || isClickable ? index + 1 : ''}
                    </StepNumberElement>
                ) : (
                    <StepIconElement
                        value={!active && completed && IconName.Done}
                        primary={active || completed}
                        active={active}
                        singleItem={singleItem}
                        completed={completed}
                        size="small"
                    />
                )}
                <StepTextWrapper active={active} vertical={vertical}>
                    <StepPrimaryTextElement active={active} clickable={isClickable} completed={completed}>
                        {label}
                    </StepPrimaryTextElement>
                    {optional && <StepSecondaryTextElement>{OptimusTextKey.Optional}</StepSecondaryTextElement>}
                </StepTextWrapper>
            </StepElement>
        </li>
    );
};
